import { useNavigate } from 'react-router-dom';
import Breadcrumb from '@/components/common/Breadcrumb';
import CSVUpload from '@/components/common/CSVUpload';
import { useToast } from '@/components/common/Toast';
import { ParsedCSVData } from '@/types/upload';
import { validateIncentiveCsv } from '@/utils/incentiveCsvValidation';

const IncentiveUpload = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleUploadSuccess = (_data: ParsedCSVData) => {
    showToast('アップロードを受け付けました。処理状況はアップロード一覧で確認してください');
    navigate('/upload-management/new');
  };

  return (
    <div>
      <Breadcrumb />
      <div className="card max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-admin-text mb-2">インセンティブCSVアップロード</h1>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-admin-text mb-2">
            📋 CSVファイルのフォーマットと注意事項
          </h2>
          <ul className="text-sm text-admin-textLight space-y-1 list-disc list-inside">
            <li>
              ファイル形式：CSV形式（.csv）のみ / サイズ 10MB 以下 / 文字コード UTF-8（BOM
              可）または Shift_JIS
            </li>
            <li>
              Coupon と同じ 3 行構成：1 行目＝カラム名 / 2 行目＝日本語定義名 / 3
              行目以降＝データ（1 行 = 1 インセンティブ）。1・2 行目はヘッダー扱いで検証しないため、
              <strong>2 行目にデータを書いても取り込まれません</strong>
            </li>
            <li>
              空行は先頭・途中・末尾のどこにあっても読み飛ばします（区切り文字だけの行
              「,,,,,,,,,,」は空行ではなくデータ行として扱われ、必須項目エラーになります）
            </li>
            <li>
              日時列（start_at / end_at）は<strong>すべて JST</strong>。形式は 2026/9/1
              0:00（日付のみ・タイムゾーン付きは不可）。end_at 空欄は無期限
            </li>
            <li>
              incentive_id は半角英数・ハイフン（-）・アンダースコア（_）の 16〜64 文字。既存 ID
              を含む CSV は上書き更新になる
            </li>
            <li>
              incentive_type は regist（会員登録）/ login（ログイン）/
              purchase（購入）。purchase_type / purchase_conditions / exclude_coupon_used は
              purchase のときのみ指定
            </li>
            <li>
              platform は 3 桁の 0/1（web / iOS / Android の順、1=対象 / 0=対象外。例:
              111）。他のアップロードと同じ形式
            </li>
            <li>
              incentive_value_type は point（ポイント数）/ percent（還元率 %）/
              page（特集ページの slug）/ coupon（coupon_id）。coupon_id
              と特集ページの実在確認は行われないため、値を確認のうえアップロードしてください
            </li>
            <li>
              <strong>
                購入特典（incentive_type = purchase）の還元は percent のみ。point
                は指定できません
              </strong>
              （point
              は割引後の残額より多いポイントを返しうるため）。逆に percent は purchase
              でのみ指定できます
            </li>
            <li>
              <strong>
                purchase（購入特典）の付与処理は未実装のため、取り込んでも付与は行われません
              </strong>
              （設定の事前登録は可能）
            </li>
            <li>
              削除の仕組みはありません。
              <strong>
                配布を止める場合は同じ incentive_id で end_at を過去日時に変更して再アップロード
              </strong>
              してください（付与済みの記録には影響しません）
            </li>
            <li>
              <strong>検証エラーが 1 件でもあると、その CSV は 1 行も反映されません</strong>
              （アップロード一覧のステータスは「FAILED」）
            </li>
            <li>
              検証を全件通過したあとの保存で一部の行だけ失敗した場合は、ステータスが
              「PARTIAL_SUCCESS」になります。反映は incentive_id 単位の上書きなので、
              <strong>同じ CSV をそのまま再アップロードすれば復旧します</strong>
            </li>
          </ul>
        </div>

        <CSVUpload
          uploadType="Incentive"
          customValidate={validateIncentiveCsv}
          onUploadSuccess={handleUploadSuccess}
        />

        <div className="flex gap-4 mt-6">
          <button
            onClick={() => navigate('/upload-management/new')}
            className="px-6 py-2 border border-gray-300 text-admin-text rounded-lg hover:bg-gray-50 transition-colors"
          >
            戻る
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncentiveUpload;
